import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { TrendingUp, Users, Package, DollarSign } from 'lucide-react';

const Analytics = () => {
  const [monthlyData, setMonthlyData] = useState([]);
  const [productSales, setProductSales] = useState([]);
  const [topRetailers, setTopRetailers] = useState([]);
  const [paymentAging, setPaymentAging] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalyticsData();
  }, []);

  const fetchAnalyticsData = async () => {
    try {
      const [monthlyRes, productsRes, retailersRes, agingRes] = await Promise.all([
        api.get('/analytics/orders/monthly'),
        api.get('/analytics/products/sales'),
        api.get('/analytics/retailers/top'),
        api.get('/analytics/payments/aging')
      ]);

      setMonthlyData(monthlyRes.data);
      setProductSales(productsRes.data);
      setTopRetailers(retailersRes.data);
      setPaymentAging(agingRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching analytics data:', error);
      setLoading(false);
    }
  };

  const COLORS = ['#FF69B4', '#FF1493', '#FF00FF', '#DA70D6', '#DDA0DD'];

  if (loading) {
    return <div className="container">Loading analytics...</div>;
  }

  return (
    <div className="container">
      <h1 style={{ marginBottom: '30px' }}>Analytics</h1>

      <div className="grid grid-2" style={{ marginBottom: '30px' }}>
        <div className="card">
          <h3 style={{ marginBottom: '20px', display: 'flex', alignItems: 'center' }}>
            <TrendingUp size={20} style={{ marginRight: '8px' }} />
            Monthly Sales Trend
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="order_count" stroke="#FF69B4" name="Orders" />
              <Line type="monotone" dataKey="total_revenue" stroke="#8884d8" name="Revenue (₹)" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="card">
          <h3 style={{ marginBottom: '20px', display: 'flex', alignItems: 'center' }}>
            <Package size={20} style={{ marginRight: '8px' }} />
            Product Sales Distribution
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={productSales}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={(entry) => entry.name}
                outerRadius={80}
                fill="#8884d8"
                dataKey="total_quantity"
              >
                {productSales.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-2" style={{ marginBottom: '30px' }}>
        <div className="card">
          <h3 style={{ marginBottom: '20px', display: 'flex', alignItems: 'center' }}>
            <Users size={20} style={{ marginRight: '8px' }} />
            Top Retailers by Revenue
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={topRetailers} layout="horizontal">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" width={100} />
              <Tooltip />
              <Bar dataKey="total_revenue" fill="#FF69B4" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="card">
          <h3 style={{ marginBottom: '20px', display: 'flex', alignItems: 'center' }}>
            <DollarSign size={20} style={{ marginRight: '8px' }} />
            Payment Aging Analysis
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={paymentAging}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="aging_bucket" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="total_amount" fill="#FF69B4" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="card">
        <h3 style={{ marginBottom: '20px' }}>Product Performance Summary</h3>
        <table className="table">
          <thead>
            <tr>
              <th>Product</th>
              <th>SKU</th>
              <th>Orders</th>
              <th>Quantity Sold</th>
              <th>Total Revenue</th>
            </tr>
          </thead>
          <tbody>
            {productSales.map(product => (
              <tr key={product.sku}>
                <td>{product.name}</td>
                <td>{product.sku}</td>
                <td>{product.order_count || 0}</td>
                <td>{product.total_quantity || 0}</td>
                <td>₹{product.total_revenue?.toFixed(2) || '0.00'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card">
        <h3 style={{ marginBottom: '20px' }}>Top Performing Retailers</h3>
        <table className="table">
          <thead>
            <tr>
              <th>Rank</th>
              <th>Retailer</th>
              <th>Status</th>
              <th>Orders</th>
              <th>Total Revenue</th>
            </tr>
          </thead>
          <tbody>
            {topRetailers.map((retailer, index) => (
              <tr key={retailer.id}>
                <td>{index + 1}</td>
                <td>{retailer.name}</td>
                <td>
                  <span className={`badge ${retailer.status === 'customer' ? 'badge-success' : 'badge-warning'}`}>
                    {retailer.status}
                  </span>
                </td>
                <td>{retailer.order_count || 0}</td>
                <td>₹{retailer.total_revenue?.toFixed(2) || '0.00'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Analytics;